import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SearchableDropdown from '../../../../../components/SearchableDropdown';
import { getWingsDropdown, getFlatsDropdown } from '../../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { labelCls, errorCls } from '../form/formStyles';

// Every VMS record is filed against a flat, and flats are only fetchable one
// wing at a time — so the two pickers are always a pair and are kept together
// here rather than re-wired in five separate forms.
//
// Choosing a different wing clears the flat: the previously selected flat
// belongs to the old wing and would otherwise be submitted against the new one.
const WingFlatPicker = ({
    societyId,
    wingId,
    flatId,
    onWingChange,
    onFlatChange,
    errors = {},
    required = true,
    disabled = false,
    // Filter bars want an empty state ("any wing"); forms do not.
    clearable = false,
    wingPlaceholder = 'Select wing',
    flatPlaceholder = 'Select flat',
}) => {
    const dispatch = useDispatch();
    const { wingsDropdown, wingsDropdownLoading, flatsDropdown, flatsDropdownLoading } =
        useSelector(state => state.WingsAndFlatsReducers) || {};

    useEffect(() => {
        if (societyId) dispatch(getWingsDropdown({ society_id: societyId }));
    }, [dispatch, societyId]);

    useEffect(() => {
        if (wingId) dispatch(getFlatsDropdown({ wing_id: wingId }));
    }, [dispatch, wingId]);

    const wingOptions = (wingsDropdown || []).map(w => ({
        value: String(w.wing_id),
        label: w.title || w.wing_name || `Wing ${w.wing_id}`,
    }));

    const flatOptions = (flatsDropdown || []).map(f => ({
        value: String(f.flat_id),
        label: f.flat_number || f.flat_no || `Flat ${f.flat_id}`,
    }));

    const star = required ? <span className="tw-text-rose-500"> *</span> : null;

    // The dropdown reports "cleared" as the literal 'all'; every caller here
    // stores '' so the id can simply be left out of the request.
    const unwrap = (v) => (v === 'all' ? '' : v);

    return (
        <>
            <div>
                <label className={labelCls}>Wing{star}</label>
                <SearchableDropdown
                    small
                    clearable={clearable}
                    disabled={disabled}
                    loading={!!wingsDropdownLoading}
                    value={wingId ? String(wingId) : ''}
                    onChange={(raw) => {
                        const v = unwrap(raw);
                        onWingChange(v);
                        // The chosen flat belongs to the old wing, so it cannot
                        // survive a wing change.
                        if (String(v) !== String(wingId)) onFlatChange('');
                    }}
                    options={wingOptions}
                    placeholder={wingPlaceholder}
                />
                {errors.wing_id && <p className={errorCls}>{errors.wing_id}</p>}
            </div>

            <div>
                <label className={labelCls}>Flat{star}</label>
                <SearchableDropdown
                    small
                    clearable={clearable}
                    disabled={disabled || !wingId}
                    loading={!!flatsDropdownLoading}
                    value={flatId ? String(flatId) : ''}
                    onChange={(raw) => onFlatChange(unwrap(raw))}
                    options={flatOptions}
                    placeholder={wingId ? flatPlaceholder : 'Pick a wing first'}
                />
                {errors.flat_id && <p className={errorCls}>{errors.flat_id}</p>}
            </div>
        </>
    );
};

export default WingFlatPicker;
